import CardContainer from '../components/cardContainer';
import Card from '../components/card';
import {
    makeStyles 
} from '@mui/styles';
import {
    Box, Slide, 
    Typography,
    useTheme
} from '@mui/material';
import {
    useEffect, useState 
} from 'react';
import {
    useCookies 
} from 'react-cookie';
import {
    useRouter 
} from 'next/router';
import {
    gaEvent 
} from '../../../lib/gtag';
import {
    postCheckoutSession 
} from '../../../logic/pricing';
import {
    getPriceId 
} from '../../../utils/pricing/purchaseLogic';
import Stripe from '../../../lib/stripe';
import {
    getCheckoutConfirmPageUrl 
} from '../../../pages/checkout-success';
import {
    compose 
} from 'underscore';
import withAuthentication from '../../authentication/withauthentication';
import withPricing from '../../../components_sunnyside/pricing_block/withPricing';
import {
    PricingDataType 
} from '../../../utils/pricing/types/pricing-data-type';
import {
    Data, get 
} from '../../../networking';
import Loading from '../../core/loading';
import {
    GetServerSidePropsContext 
} from 'next';
import {
    TypographyVariants, TypographyWeights 
} from '../../../figmaTheme';

type Props = {
    pricingData: PricingDataType,
    screenName: string
};

async function getCouponInfo(id?: string, context?: GetServerSidePropsContext): Promise<Data> {
    if (!id) {
        return;
    }
    return await get(`payments-gateway/stripe/coupon/${id}`, {}, context);
}

const useStyles = makeStyles((theme) => ({
    scrollableContainer: {
        overflow: 'scroll',
        scrollbarWidth: 'none',
        minHeight: '100%',
        width: '100vw',
        maxWidth: '692px',
        transform: 'translate(-24px, 0)',
        padding: '0 24px'
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    title: {
        fontWeight: TypographyWeights.SEMI_BOLD,
        textAlign: 'center',
        position: 'relative',
        margin: '0 0 40px'
    },
    subscriptionBox: {
        width: '100%',
        backgroundColor: theme.palette.primary.light,
        border: `2px solid ${theme.palette.primary.main}`,
        borderRadius: '16px',
        padding: '24px'
    },
    label: {
        position: 'absolute',
        fontWeight: TypographyWeights.SEMI_BOLD,
        backgroundColor: theme.palette.tertiary.main,
        padding: '4px 12px',
        width: 'fit-content',
        borderRadius: '4px',
        transform: 'translate(0, -36px)'
    },
    planInformation: {
        marginBottom: '16px'
    },
    planFeatures: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: '8px'
    },
    feature: {
        display: 'grid',
        gridTemplateColumns: '16px 1fr',
        gridGap: '8px',
    }
}));

export const Checkout = ({
    pricingData
}: Props): JSX.Element => {
    const classes = useStyles();
    const theme = useTheme();

    const [loading, setLoading] = useState(true);
    const [cookies] = useCookies(['_fbc', '_fbp', 'ct', 'td']);
    const router = useRouter();

    const [confirmPageUrl, setConfirmPageUrl] = useState('');
    const [isGeneratingCheckout, setIsGeneratingCheckout] = useState(false);
    const upgradeCheckoutTypes = ['iu', 'sp'];

    const [couponResponse, setCouponResponse] = useState<Data | null>(null);

    const onClick = () => {
        postNewCheckoutSession();
    };

    const handleActiveNonStripeUsers = () => {
        router.push('/dashboard');
    };

    const postNewCheckoutSession = async () => {
        /* Prevent multiple requests */
        if (isGeneratingCheckout) return;
        try {
            setIsGeneratingCheckout(true);
            const priceId = getPriceId(pricingData, 'year');
            const payload = {
                priceId: priceId,
                successUrl: confirmPageUrl,
                cancelUrl: window ? window.location.href : ''
            };
            const session = await postCheckoutSession(payload);

            const stripe = await Stripe;
            if (session.url) {
                router.push(session.url);
            } else if (session.id) {
                stripe.redirectToCheckout({
                    sessionId: session.id as string,
                });
            } else if (session.statusCode === 409) {
                handleActiveNonStripeUsers();
            }
        } catch (e) {
            setIsGeneratingCheckout(false);
        }
    };

    useEffect(() => {
        if (pricingData) {
            if (pricingData.checkout_info.checkout_type === 'ptcc') {
                setConfirmPageUrl(
                    encodeURIComponent(
                        window ?
                            window.location.protocol + '//' + window.location.host + getCheckoutConfirmPageUrl({
                                redirect: '/start/confirm'
                            })
                            :
                            ''));
            } else if (upgradeCheckoutTypes.includes(pricingData.checkout_info.checkout_type)) {
                setConfirmPageUrl(
                    encodeURIComponent(
                        window ?
                            window.location.protocol + '//' + window.location.host + getCheckoutConfirmPageUrl({
                                url: '/subscription/confirm-upgrade'
                            })
                            :
                            ''));
            } else {
                setConfirmPageUrl(
                    encodeURIComponent(
                        window ?
                            window.location.protocol + '//' + window.location.host + getCheckoutConfirmPageUrl()
                            :
                            ''));
            }
        }
    }, [pricingData]);

    useEffect(() => {
        gaEvent({
            action: 'Page',
            category: 'Land',
            label: 'signup: landed on checkout - your recommendation',
            value: '',
        });

        setLoading(false);
    }, []);

    useEffect(() => {
        async function getCoupon() {
            const couponResponse = await getCouponInfo(cookies.ss_discount);
            setCouponResponse(couponResponse);
        }
        getCoupon();
    }, [cookies.ss_discount]);

    useEffect(() => {
        if (cookies.ct === 'no_cc') router.push('/subscription/no-cc');
    }, [cookies]);

    if (cookies.ct === 'no_cc' || loading || !pricingData) {
        return (
            <Box>
                <Loading />
            </Box>
        );
    }

    return (
        <CardContainer
            sectionKey={'checkout'}
            goNext={onClick}
            hideArrowButton
            showCTAButton
            hasGradient
            buttonText={'Try Free & Subscribe'}
            ctaText={`Try 15 days free, then $${pricingData.computed_prices?.annual_plan_price_with_discount.toFixed(2)}/year. Cancel any time.`}
        >
            <Card>
                <Box className={classes.scrollableContainer}>
                    <Slide
                        in={true}
                        direction={'left'}
                        timeout={250}
                        mountOnEnter
                    >
                        <Box className={classes.content}>
                            <Typography
                                variant={TypographyVariants.H2}
                                className={classes.title}
                                dangerouslySetInnerHTML={{
                                    __html: 'Confirm your Sunnyside membership' 
                                }}
                            />
                            <Box className={classes.subscriptionBox}>
                                <Typography
                                    variant={TypographyVariants.SMALL}
                                    className={classes.label}
                                    dangerouslySetInnerHTML={{
                                        __html: couponResponse?.percent_off ? `${couponResponse.percent_off}% DISCOUNT APPLIED` : 'Recommended for You' 
                                    }}
                                />
                                <Box className={classes.planInformation}>
                                    <Typography
                                        variant={TypographyVariants.H5}
                                        dangerouslySetInnerHTML={{
                                            __html: 'Your membership to build better drinking habits that last' 
                                        }}
                                    />
                                </Box>
                                <Box className={classes.planFeatures}>
                                    <Box className={classes.feature}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.3378 6.34813C11.5852 6.05113 11.5451 5.60974 11.2481 5.36224C10.9511 5.11475 10.5097 5.15487 10.2622 5.45187L7.25296 9.06301L5.69497 7.50502C5.42161 7.23165 4.97839 7.23165 4.70503 7.50502C4.43166 7.77839 4.43166 8.2216 4.70503 8.49497L6.80503 10.595C6.94409 10.734 7.13525 10.8082 7.33172 10.7993C7.52819 10.7904 7.71185 10.6992 7.83775 10.5481L11.3378 6.34813Z"
                                                fill={theme.palette.success.main}
                                            />
                                        </svg>
                                        <Typography
                                            variant={TypographyVariants.SMALL}
                                            dangerouslySetInnerHTML={{
                                                __html: 'Free 15-day free trial' 
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.feature}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.3378 6.34813C11.5852 6.05113 11.5451 5.60974 11.2481 5.36224C10.9511 5.11475 10.5097 5.15487 10.2622 5.45187L7.25296 9.06301L5.69497 7.50502C5.42161 7.23165 4.97839 7.23165 4.70503 7.50502C4.43166 7.77839 4.43166 8.2216 4.70503 8.49497L6.80503 10.595C6.94409 10.734 7.13525 10.8082 7.33172 10.7993C7.52819 10.7904 7.71185 10.6992 7.83775 10.5481L11.3378 6.34813Z"
                                                fill={theme.palette.success.main}
                                            />
                                        </svg>
                                        <Typography
                                            variant={TypographyVariants.SMALL}
                                            dangerouslySetInnerHTML={{
                                                __html: `Only ${pricingData.computed_prices?.annual_plan_price_per_month_with_discount.toFixed(2)} / month, less than the cost of a drink` 
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.feature}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.3378 6.34813C11.5852 6.05113 11.5451 5.60974 11.2481 5.36224C10.9511 5.11475 10.5097 5.15487 10.2622 5.45187L7.25296 9.06301L5.69497 7.50502C5.42161 7.23165 4.97839 7.23165 4.70503 7.50502C4.43166 7.77839 4.43166 8.2216 4.70503 8.49497L6.80503 10.595C6.94409 10.734 7.13525 10.8082 7.33172 10.7993C7.52819 10.7904 7.71185 10.6992 7.83775 10.5481L11.3378 6.34813Z"
                                                fill={theme.palette.success.main}
                                            />
                                        </svg>
                                        <Typography
                                            variant={TypographyVariants.SMALL}
                                            dangerouslySetInnerHTML={{
                                                __html: 'Cancel at any time' 
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.feature}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.3378 6.34813C11.5852 6.05113 11.5451 5.60974 11.2481 5.36224C10.9511 5.11475 10.5097 5.15487 10.2622 5.45187L7.25296 9.06301L5.69497 7.50502C5.42161 7.23165 4.97839 7.23165 4.70503 7.50502C4.43166 7.77839 4.43166 8.2216 4.70503 8.49497L6.80503 10.595C6.94409 10.734 7.13525 10.8082 7.33172 10.7993C7.52819 10.7904 7.71185 10.6992 7.83775 10.5481L11.3378 6.34813Z"
                                                fill={theme.palette.success.main}
                                            />
                                        </svg>
                                        <Typography
                                            variant={TypographyVariants.SMALL}
                                            dangerouslySetInnerHTML={{
                                                __html: 'Save $50 average in the first 30 days' 
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.feature}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.3378 6.34813C11.5852 6.05113 11.5451 5.60974 11.2481 5.36224C10.9511 5.11475 10.5097 5.15487 10.2622 5.45187L7.25296 9.06301L5.69497 7.50502C5.42161 7.23165 4.97839 7.23165 4.70503 7.50502C4.43166 7.77839 4.43166 8.2216 4.70503 8.49497L6.80503 10.595C6.94409 10.734 7.13525 10.8082 7.33172 10.7993C7.52819 10.7904 7.71185 10.6992 7.83775 10.5481L11.3378 6.34813Z"
                                                fill={theme.palette.success.main}
                                            />
                                        </svg>
                                        <Typography
                                            variant={TypographyVariants.SMALL}
                                            dangerouslySetInnerHTML={{
                                                __html: 'Improve your health, cut calories and sleep better' 
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                            <Box
                                style={{
                                    height: '500px' 
                                }}
                            />
                        </Box>
                    </Slide>
                </Box>
            </Card>
        </CardContainer>
    );
};

export default compose(withAuthentication, withPricing)(Checkout);
